import { useEffect, useState } from 'react';
import api from '@/lib/api';

let _cache = null;
let _inflight = null;

// Public catalog (version, copy, matrix, real cost examples). Cached for the session — one fetch per page load.
export function useCatalog() {
  const [catalog, setCatalog] = useState(_cache);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (_cache) return;
    _inflight = _inflight || api.get('/billing/catalog').then((r) => { _cache = r.data; return r.data; });
    _inflight.then(setCatalog).catch((e) => setError(e));
  }, []);
  return { catalog, loading: !catalog && !error, error };
}

export const fmtLimit = (v, { unlimitedWord = 'Unlimited' } = {}) => (v === null || v === undefined ? unlimitedWord : Number(v).toLocaleString());

export const PLAN_ORDER = ['starter', 'growth', 'command', 'scale', 'enterprise'];

// Bullet lines for a plan card, derived from the live matrix (shared by Settings → Billing and /upgrade).
export const planFeatureLines = (m) => {
  const L = m.limits;
  return [
    `${fmtLimit(L.businesses)} business${L.businesses === 1 ? '' : 'es'} — each with its own SEO & GEO site · ${fmtLimit(L.campaigns)} active ad campaigns`,
    `${fmtLimit(L.hi_contacts)} high-intent leads`,
    `${fmtLimit(L.studio_credits)} Creative Studio · ${fmtLimit(L.ai_credits)} AI · ${fmtLimit(L.voice_credits)} voice credits/mo`,
    `${fmtLimit(L.funnels)} active funnels · ${fmtLimit(L.landing_pages)} landing pages · ${fmtLimit(L.nurture)} nurture campaigns`,
    `${fmtLimit(L.mailboxes)} connected mailboxes · ${fmtLimit(L.widgets)} chat widgets`,
    `${fmtLimit(L.teammates)} additional teammate${L.teammates === 1 ? '' : 's'}${L.coach_seats ? ` · ${fmtLimit(L.coach_seats)} Sales Coach users` : ''}`,
    ...(m.features.demand_map ? ['Demand Map · GEO Engine · proposals · competitor tracking'] : []),
    ...(m.features.health_monitor ? ['Business Health Monitor'] : []),
  ];
};

// Card-level grouping of the matrix for the pricing page + settings cards
export const MATRIX_GROUPS = [
  { title: 'Capacity', keys: ['businesses', 'campaigns', 'teammates', 'coach_seats'] },
  { title: 'Monthly consumables', keys: ['hi_contacts', 'studio_credits', 'ai_credits', 'voice_credits'] },
  { title: 'Marketing assets', keys: ['funnels', 'landing_pages', 'nurture', 'widgets', 'mailboxes'] },
];

export const CONSUMABLE_HELP = {
  hi_contacts: 'How many high-intent leads you can track at the same time across your lists — a total capacity, not a monthly allowance. A tracked lead uses capacity while it is on a list and frees it when it drops off (or a month after the list is deleted). Your own imported contacts never use it.',
  studio_credits: 'One shared Creative Studio balance for every generated image, video and voice-over clip — whether Vela makes it for social, for an ad, or you make it by hand. Uploading your own media or scheduling a post costs nothing.',
  ai_credits: 'General AI work — Vela chat, captions, ad copy, SEO audits, page drafts, decks. Each action has a published cost; the same action is never billed twice.',
  voice_credits: 'AI narration. Charged per character of script at the voice tier\'s rate — no fixed "minutes" are promised because speech length depends on the script.',
};
